import React from "react";
import {
  ChatMessage as ChatMessageType,
  MessageFeedback,
  ImageAttachment,
} from "../types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiService } from "../services/api";
import ImageModal from "./ImageModal";
import {
  Bot,
  User,
  Clock,
  TrendingUp,
  Database,
  Lightbulb,
  Copy,
  Check,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";

interface ChatMessageProps {
  message: ChatMessageType;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant";
  const [copied, setCopied] = React.useState(false);
  const [feedback, setFeedback] = React.useState<MessageFeedback | null>(null);
  const [feedbackLoading, setFeedbackLoading] = React.useState(false);
  const [showFeedbackToast, setShowFeedbackToast] = React.useState(false);
  const [showCommentBox, setShowCommentBox] = React.useState(false);
  const [feedbackComment, setFeedbackComment] = React.useState("");
  const [pendingFeedbackType, setPendingFeedbackType] = React.useState<
    "thumbs_up" | "thumbs_down" | null
  >(null);
  const [selectedImage, setSelectedImage] =
    React.useState<ImageAttachment | null>(null);

  // Load existing feedback on component mount
  const loadExistingFeedback = React.useCallback(async () => {
    try {
      const existingFeedback = await apiService.getMessageFeedback(message.id);
      if (existingFeedback) {
        setFeedback(existingFeedback);
        // If there's a comment, store it (for display purposes)
        if (existingFeedback.comment) {
          setFeedbackComment(existingFeedback.comment);
        }
      }
    } catch (error) {
      console.error("Error loading feedback:", error);
    }
  }, [message.id]);

  React.useEffect(() => {
    if (isAssistant && message.id) {
      loadExistingFeedback();
    }
  }, [isAssistant, message.id, loadExistingFeedback]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFeedback = async (feedbackType: "thumbs_up" | "thumbs_down") => {
    if (!message.id || !message.thread_id || feedbackLoading) return;

    // If feedback already exists for this message, don't show comment box
    if (feedback?.has_feedback) return;

    // Show comment box and store the feedback type
    setPendingFeedbackType(feedbackType);
    setShowCommentBox(true);
  };

  const handleSubmitFeedback = async () => {
    if (
      !message.id ||
      !message.thread_id ||
      !pendingFeedbackType ||
      feedbackLoading
    )
      return;

    setFeedbackLoading(true);
    try {
      await apiService.submitFeedback(
        message.id,
        message.thread_id,
        pendingFeedbackType,
        undefined,
        feedbackComment.trim() || undefined
      );

      // Update local feedback state
      setFeedback({
        has_feedback: true,
        feedback_type: pendingFeedbackType,
        comment: feedbackComment.trim() || undefined,
        created_at: new Date().toISOString(),
      });

      // Show success toast
      setShowFeedbackToast(true);
      setTimeout(() => setShowFeedbackToast(false), 3000);

      // Reset comment box
      setShowCommentBox(false);
      setFeedbackComment("");
      setPendingFeedbackType(null);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      // Could show error toast here
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleCancelFeedback = () => {
    setShowCommentBox(false);
    setFeedbackComment("");
    setPendingFeedbackType(null);
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-6`}>
      <div
        className={`flex max-w-[85%] ${
          isUser ? "flex-row-reverse" : "flex-row"
        }`}
      >
        {/* Avatar */}
        <div className={`flex-shrink-0 ${isUser ? "ml-4" : "mr-4"}`}>
          <div
            className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-lg ${
              isUser
                ? "bg-gradient-to-br from-primary-500 to-primary-600 text-white"
                : "bg-gradient-to-br from-white dark:from-neutral-800 to-primary-50 dark:to-orange-900/20 text-primary-600 dark:text-orange-400 border border-primary-200 dark:border-orange-800/30"
            }`}
          >
            {isUser ? <User size={18} /> : <Bot size={18} />}
          </div>
        </div>

        {/* Message Content */}
        <div className="flex-1">
          <div
            className={`rounded-3xl px-6 py-4 shadow-soft transition-all duration-300 ${
              isUser
                ? "bg-gradient-to-br from-primary-500 to-primary-600 text-white"
                : "bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 border border-neutral-200 dark:border-neutral-700"
            }`}
          >
            {/* Image Attachments - Only for user messages */}
            {isUser &&
              message.image_attachments &&
              message.image_attachments.length > 0 && (
                <div className="mb-3 space-y-2">
                  {message.image_attachments.map((img, idx) => {
                    // Handle preview URLs (object URLs) vs server URLs
                    let imageUrl = "";
                    if (
                      img.file_path.startsWith("blob:") ||
                      img.file_path.startsWith("data:")
                    ) {
                      // Preview URL from File object
                      imageUrl = img.file_path;
                    } else if (message.thread_id) {
                      // Server URL from backend
                      imageUrl = apiService.getImageUrl(
                        message.thread_id,
                        img.filename
                      );
                    }
                    return (
                      <div key={idx} className="relative">
                        <img
                          src={imageUrl}
                          alt={img.filename}
                          className="max-w-[400px] rounded-lg cursor-pointer hover:opacity-90 transition-opacity border border-white/20"
                          onClick={() => setSelectedImage(img)}
                        />
                        <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-xs flex items-center gap-1">
                          <span>📷</span>
                          Image
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

            {/* Message Text */}
            <div className="text-sm leading-relaxed">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  ol: ({ children }) => (
                    <ol className="list-decimal list-outside mb-4 space-y-2 ml-6">
                      {children}
                    </ol>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc list-outside mb-4 space-y-1 ml-6">
                      {children}
                    </ul>
                  ),
                  li: ({ children }) => (
                    <li
                      className={`leading-relaxed pl-2 ${
                        isUser
                          ? "text-white"
                          : "text-neutral-800 dark:text-neutral-200"
                      }`}
                    >
                      {children}
                    </li>
                  ),
                  p: ({ children }) => (
                    <p
                      className={`mb-2 last:mb-0 ${
                        isUser
                          ? "text-white"
                          : "text-neutral-800 dark:text-neutral-200"
                      }`}
                    >
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong
                      className={
                        isUser
                          ? "text-white font-semibold"
                          : "text-neutral-900 dark:text-neutral-100 font-semibold"
                      }
                    >
                      {children}
                    </strong>
                  ),
                  em: ({ children }) => (
                    <em
                      className={
                        isUser
                          ? "text-white/90"
                          : "text-neutral-700 dark:text-neutral-300"
                      }
                    >
                      {children}
                    </em>
                  ),
                  code: ({ children }) => (
                    <code
                      className={`px-1.5 py-0.5 rounded text-sm font-mono ${
                        isUser
                          ? "bg-white/20 text-white"
                          : "bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200"
                      }`}
                    >
                      {children}
                    </code>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>

            {/* Assistant-specific metadata */}
            {isAssistant && (
              <div className="mt-4 pt-4 border-t border-neutral-200/50 dark:border-neutral-700/50">
                {/* Metadata Row */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3 text-xs">
                    {/* Confidence Score - Hidden */}
                    {/* {message.confidence_score !== undefined && (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 dark:bg-orange-900/20 rounded-lg">
                        <TrendingUp
                          size={12}
                          className="text-primary-500 dark:text-orange-400"
                        />
                        <span className="text-primary-700 dark:text-orange-300 font-medium">
                          {Math.round(message.confidence_score * 100)}%
                          confidence
                        </span>
                      </div>
                    )} */}

                    {/* Components Found */}
                    {message.components_found !== undefined && (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary-50 dark:bg-yellow-900/20 rounded-lg">
                        <Database
                          size={12}
                          className="text-secondary-500 dark:text-yellow-400"
                        />
                        <span className="text-secondary-700 dark:text-yellow-300 font-medium">
                          {message.components_found} components
                        </span>
                      </div>
                    )}

                    {/* Timestamp */}
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                      <Clock
                        size={12}
                        className="text-neutral-500 dark:text-neutral-400"
                      />
                      <span className="text-neutral-600 dark:text-neutral-300 font-medium">
                        {message.timestamp.toLocaleTimeString(undefined, {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Copy Button */}
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-all duration-200 text-xs font-medium"
                  >
                    {copied ? (
                      <>
                        <Check size={12} className="text-success-500" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy size={12} />
                        Copy
                      </>
                    )}
                  </button>
                </div>

                {/* Follow-up Suggestions */}
                {message.follow_up_suggestions &&
                  message.follow_up_suggestions.length > 0 && (
                    <div className="mb-3">
                      <div className="flex items-center gap-2 text-xs text-neutral-600 dark:text-neutral-300 mb-3">
                        <Lightbulb
                          size={14}
                          className="text-accent-500 dark:text-red-400"
                        />
                        <span className="font-medium">
                          Suggested follow-ups:
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {message.follow_up_suggestions.map(
                          (suggestion, index) => (
                            <span
                              key={index}
                              className="px-3 py-1.5 bg-gradient-to-r from-accent-50 to-accent-100 dark:from-red-900/20 dark:to-red-800/20 text-accent-700 dark:text-red-300 rounded-lg text-xs font-medium hover:from-accent-100 hover:to-accent-200 dark:hover:from-red-900/30 dark:hover:to-red-800/30 transition-all duration-200 cursor-pointer"
                            >
                              {suggestion}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  )}

                {/* Feedback Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleFeedback("thumbs_up")}
                    disabled={feedbackLoading || feedback?.has_feedback}
                    className={`p-2 rounded-lg transition-all duration-200 ${
                      feedback?.feedback_type === "thumbs_up"
                        ? "text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30"
                        : "text-neutral-400 dark:text-neutral-500 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20"
                    } ${
                      feedbackLoading || feedback?.has_feedback
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    title={
                      feedback?.feedback_type === "thumbs_up"
                        ? "You liked this response"
                        : "Like this response"
                    }
                  >
                    <ThumbsUp size={14} />
                  </button>
                  <button
                    onClick={() => handleFeedback("thumbs_down")}
                    disabled={feedbackLoading || feedback?.has_feedback}
                    className={`p-2 rounded-lg transition-all duration-200 ${
                      feedback?.feedback_type === "thumbs_down"
                        ? "text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30"
                        : "text-neutral-400 dark:text-neutral-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    } ${
                      feedbackLoading || feedback?.has_feedback
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    title={
                      feedback?.feedback_type === "thumbs_down"
                        ? "You disliked this response"
                        : "Dislike this response"
                    }
                  >
                    <ThumbsDown size={14} />
                  </button>
                </div>

                {/* Feedback Comment Box */}
                {showCommentBox && (
                  <div className="mt-3 p-3 bg-neutral-50 dark:bg-neutral-700/30 rounded-lg border border-neutral-200 dark:border-neutral-600 animate-fade-in">
                    <div className="mb-2">
                      <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300 block mb-1">
                        {pendingFeedbackType === "thumbs_up"
                          ? "What did you like about this response? (optional)"
                          : "How can we improve this response? (optional)"}
                      </label>
                      <textarea
                        value={feedbackComment}
                        onChange={(e) => setFeedbackComment(e.target.value)}
                        placeholder="Add your comments here..."
                        rows={3}
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 resize-none"
                        autoFocus
                      />
                    </div>
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={handleCancelFeedback}
                        disabled={feedbackLoading}
                        className="px-3 py-1.5 text-xs font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmitFeedback}
                        disabled={feedbackLoading}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {feedbackLoading ? (
                          <>
                            <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          "Submit Feedback"
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Display Existing Feedback Comment */}
                {feedback?.has_feedback &&
                  feedback?.comment &&
                  !showCommentBox && (
                    <div className="mt-2 p-2 bg-neutral-50 dark:bg-neutral-700/20 rounded-lg border border-neutral-200 dark:border-neutral-600">
                      <p className="text-xs text-neutral-600 dark:text-neutral-400 italic">
                        Your comment: "{feedback.comment}"
                      </p>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Feedback Toast */}
      {showFeedbackToast && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <div className="bg-green-600 dark:bg-green-700 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
            <Check size={16} />
            <span className="text-sm font-medium">
              Feedback submitted! Thank you for helping us improve.
            </span>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && message.thread_id && (
        <ImageModal
          image={selectedImage}
          threadId={message.thread_id}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
};

export default ChatMessage;
